import { ChangeEvent, FormEvent, forwardRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  FormControl,
  FormLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { MuiTelInput } from "mui-tel-input";

import AutocompleteIcon from "../../assets/icons/MUI/ComponentsIcons";
import { entityRoles, entityTypes, numberOfMembersRanges } from "../../constants/prefilled.constants";
import { ClosePopupIcon } from "../../assets/icons/MUI/EntitiesIcons";
import { registerYourEntity } from "../../api/home";
import { useCountryListing } from "../../api/queries/network/useCountryListing";
import RequestSentImg from "../../assets/images/Home/RequestSent.png";

const formLabelStyle = {
  color: "var(--White, #FFF)",
  fontFamily: "Montserrat",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "20px",
};

const formControlStyle = {
  display: "flex",
  flexDirection: "column",
  rowGap: "20px",
  width: "100%",
};

const textFieldStyle = {
  fontFamily: "Montserrat",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
  color: "#A0A0A0",
  width: {
    xs: "100%",
    lg: "345px",
  },

  "& .MuiInputBase-root": {
    border: "1px solid #1E2021 !important",
    background: "rgba(28, 28, 28, 0.80)",
  },
  "& .MuiInputBase-input": {
    color: "#FFF",
  },
};

type FormData = {
  name: string;
  email: string;
  phoneNumber: string;
  countryId: number | null;
  entityType: string;
  entityName: string;
  entityWebsite: string;
  numberOfMembers: string;
  role: string;
  message: string;
};

const initialFormState = {
  name: "",
  email: "",
  phoneNumber: "",
  countryId: null,
  entityType: "",
  entityName: "",
  entityWebsite: "",
  numberOfMembers: "",
  role: "",
  message: "",
};

const RegisterEntity = forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, ref) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [country, setCountry] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [entityType, setEntityType] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [role, setRole] = useState<{ label: string; value: string } | null>(null);
  const [numberOfMembers, setNumberOfMembers] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState<boolean>(false);
  const [errors, setErrors] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    entityName: "",
    entityWebsite: "",
    countryId: "",
    entityType: "",
    numberOfMembers: "",
    role: "",
    message: "",
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {
      name: "",
      phoneNumber: "",
      email: "",
      entityName: "",
      entityWebsite: "",
      countryId: "",
      entityType: "",
      numberOfMembers: "",
      role: "",
      message: "",
    };

    if (!formData.name) {
      newErrors.name = t("pages.home.nameRequired");
      valid = false;
    }
    if (formData.name.length > 100) {
      newErrors.name = t("pages.home.nameMaxLengthError");
      valid = false;
    }
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = t("pages.home.phoneNumberRequired");
      valid = false;
    }
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("pages.home.validEmailRequired");
      valid = false;
    }
    if (!formData.entityName) {
      newErrors.entityName = t("pages.home.entityNameRequired");
      valid = false;
    }
    if (formData.entityName.length > 200) {
      newErrors.entityName = t("pages.home.entityMaxLengthError");
      valid = false;
    }
    if (
      !formData.entityWebsite ||
      !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z0-9-]{2,}(\/[a-zA-Z0-9-._~:\/?#[\]@!$&'()*+,;%=]*)?$/.test(
        formData.entityWebsite
      )
    ) {
      newErrors.entityWebsite = t("pages.home.entityValidWebsiteRequired");
      valid = false;
    }
    if (!formData.countryId) {
      newErrors.countryId = t("pages.home.countryRequired");
      valid = false;
    }
    if (!formData.entityType) {
      newErrors.entityType = t("pages.home.entityTypeRequired");
      valid = false;
    }
    if (!formData.numberOfMembers) {
      newErrors.numberOfMembers = t("pages.home.numberOfMembersRequired");
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await registerYourEntity(formData);
      setRequestDialogOpen(true);
      setFormData(initialFormState);
      setCountry(null);
      setEntityType(null);
      setRole(null);
      setNumberOfMembers(null);
    } catch (error: any) {
      if (error.response && error.response.status === 400 && error.response.data.errors) {
        const errorMessages = error.response.data.errors.join(", ");
        toast.error(errorMessages);
      } else {
        const errorMessage = error.message || "An unexpected error occurred. Please try again.";
        toast.error(errorMessage);
      }
    }
  };

  const { countries, isCountriesDataLoading } = useCountryListing();

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        padding: "94px 0 77px",
        background: "#101010",
      }}
    >
      <Box
        sx={{
          width: "95%",
          maxWidth: "1240px",
          margin: "0 auto",
          display: "flex",
          gap: "56px",
          justifyContent: "space-between",
          alignItems: {
            xs: "center",
            lg: "flex-start",
          },
          flexDirection: {
            xs: "column",
            lg: "row",
          },
          rowGap: "56px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: {
              xs: "16px",
              lg: "26px",
            },
            maxWidth: {
              xs: "740px",
              lg: "465px",
            },
          }}
        >
          <Typography
            component="h2"
            sx={{
              color: "#FFF",
              fontFamily: "HeadingNowVarTrial",
              fontStyle: "normal",
              fontWeight: 750,
              lineHeight: "130%",
              textTransform: "uppercase",
              fontSize: {
                xs: "30px",
                lg: "40px",
              },
            }}
          >
            {t("pages.home.readyToRegister")}
          </Typography>
          <Typography
            component="h4"
            sx={{
              color: "#FFF",
              fontFamily: "HeadingNowVarTrial",
              fontStyle: "normal",
              fontWeight: 750,
              lineHeight: "normal",
              fontSize: {
                xs: "20px",
                lg: "30px",
              },
            }}
          >
            {t("pages.home.contactTeam")}
          </Typography>
          <Typography
            component="p"
            sx={{
              color: "var(--White, #FFF)",
              fontFamily: "Montserrat",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "28px",
              fontSize: {
                xs: "16px",
                lg: "18px",
              },
            }}
          >
            {t("pages.home.dedicatedSupport")}
          </Typography>
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "35px",
            width: "100%",
            maxWidth: "740px",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              columnGap: "50px",
              rowGap: "20px",
              flexDirection: {
                xs: "column",
                lg: "row",
              },
            }}
          >
            <FormControl sx={formControlStyle}>
              <FormLabel sx={formLabelStyle}>{t("pages.home.responsiblePerson")}</FormLabel>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t("pages.home.enterYourName")}
                error={!!errors.name}
                helperText={errors.name}
                sx={textFieldStyle}
                inputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
              />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <FormLabel sx={formLabelStyle}>{t("pages.home.phoneNumber")}</FormLabel>
              <MuiTelInput
                inputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={(newValue: string) => {
                  setFormData({
                    ...formData,
                    phoneNumber: newValue.replace(/\s+/g, ""),
                  });
                  setErrors({ ...errors, phoneNumber: "" });
                }}
                sx={textFieldStyle}
                defaultCountry="SA"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
              />
            </FormControl>
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              columnGap: "50px",
              rowGap: "20px",
              flexDirection: {
                xs: "column",
                lg: "row",
              },
            }}
          >
            <FormControl sx={formControlStyle}>
              <FormLabel sx={formLabelStyle}>{t("pages.home.emailAddress")}</FormLabel>
              <TextField
                inputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t("pages.home.enterEmail")}
                sx={textFieldStyle}
                error={!!errors.email}
                helperText={errors.email}
              />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <FormLabel sx={formLabelStyle}>{t("pages.home.selectCountry")}</FormLabel>
              <Autocomplete
                options={countries || []}
                disabled={isCountriesDataLoading}
                value={country}
                onChange={(event, option) => {
                  setErrors({ ...errors, countryId: "" });
                  setCountry(option || null);
                  setFormData({
                    ...formData,
                    countryId: option?.value ? Number(option.value) : null,
                  });
                }}
                popupIcon={<AutocompleteIcon />}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={textFieldStyle}
                    placeholder={t("pages.home.selectCountryPrompt")}
                    error={!!errors.countryId}
                    helperText={errors.countryId}
                  />
                )}
              />
            </FormControl>
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              columnGap: "50px",
              rowGap: "20px",
              flexDirection: {
                xs: "column",
                lg: "row",
              },
            }}
          >
            <FormControl sx={formControlStyle}>
              <FormLabel sx={formLabelStyle}>{t("pages.home.entityType")}</FormLabel>
              <Autocomplete
                options={entityTypes}
                value={entityType}
                onChange={(event, option) => {
                  setErrors({ ...errors, entityType: "" });
                  setEntityType(option || null);
                  setFormData({ ...formData, entityType: option?.value || "" });
                }}
                popupIcon={<AutocompleteIcon />}
                getOptionLabel={(option) => t(`enums.entity.entityType.${option.label}`)}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    sx={textFieldStyle}
                    {...params}
                    error={!!errors.entityType}
                    helperText={errors.entityType}
                    placeholder={t("pages.home.selectEntityType")}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <FormLabel sx={formLabelStyle}>{t("pages.home.entityName")}</FormLabel>
              <TextField
                name="entityName"
                value={formData.entityName}
                onChange={handleChange}
                placeholder={t("pages.home.organizationNameFC")}
                sx={textFieldStyle}
                error={!!errors.entityName}
                helperText={errors.entityName}
                inputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
              />
            </FormControl>
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              columnGap: "50px",
              rowGap: "20px",
              flexDirection: {
                xs: "column",
                lg: "row",
              },
            }}
          >
            <FormControl sx={formControlStyle}>
              <FormLabel sx={formLabelStyle}>{t("pages.home.registeredMembers")}</FormLabel>
              <Autocomplete
                options={numberOfMembersRanges}
                value={numberOfMembers}
                onChange={(event, option) => {
                  setErrors({ ...errors, numberOfMembers: "" });
                  setNumberOfMembers(option || null);
                  setFormData({
                    ...formData,
                    numberOfMembers: option?.value || "",
                  });
                }}
                popupIcon={<AutocompleteIcon />}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      autocomplete: "new-password",
                      form: {
                        autocomplete: "off",
                      },
                    }}
                    placeholder={t("pages.home.selectNumber")}
                    sx={textFieldStyle}
                    error={!!errors.numberOfMembers}
                    helperText={errors.numberOfMembers}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <FormLabel sx={formLabelStyle}>{t("pages.home.yourRole")}</FormLabel>
              <Autocomplete
                options={entityRoles}
                value={role}
                onChange={(event, option) => {
                  setErrors({ ...errors, role: "" });
                  setRole(option || null);
                  setFormData({
                    ...formData,
                    role: option?.value.toUpperCase() || "",
                  });
                }}
                popupIcon={<AutocompleteIcon />}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    sx={textFieldStyle}
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      autocomplete: "new-password",
                      form: {
                        autocomplete: "off",
                      },
                    }}
                    placeholder={t("pages.home.selectYourRole")}
                    error={!!errors.role}
                    helperText={errors.role}
                  />
                )}
              />
            </FormControl>
          </Box>
          <FormControl sx={formControlStyle}>
            <FormLabel sx={formLabelStyle}>{t("pages.home.entityWebsite")}</FormLabel>
            <TextField
              name="entityWebsite"
              value={formData.entityWebsite}
              onChange={handleChange}
              placeholder={t("pages.home.enterYourWebsite")}
              error={!!errors.entityWebsite}
              helperText={errors.entityWebsite}
              sx={{
                ...textFieldStyle,
                width: "100%",
                "& .MuiInputBase-root": {
                  borderRadius: "16px !important",
                  border: "1px solid #1E2021 !important",
                  background: "rgba(28, 28, 28, 0.80)",
                },
              }}
              inputProps={{
                autocomplete: "new-password",
                form: {
                  autocomplete: "off",
                },
              }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <FormLabel sx={formLabelStyle}>{t("pages.home.message")}</FormLabel>
            <TextField
              inputProps={{
                autocomplete: "new-password",
                form: {
                  autocomplete: "off",
                },
              }}
              multiline
              rows={5}
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t("pages.home.enterMessage")}
              error={!!errors.message}
              helperText={errors.message}
              sx={{
                ...textFieldStyle,
                width: "100%",
                "& .MuiInputBase-root": {
                  borderRadius: "8px !important",
                  border: "1px solid #1E2021 !important",
                  background: "rgba(28, 28, 28, 0.80)",
                },
              }}
            />
          </FormControl>

          <Button
            type="submit"
            variant="primary-lg"
            sx={{
              width: {
                xs: "100%",
                lg: "fit-content",
              },
              marginLeft: "auto",
              display: "block",
              background: "var(--Green---100, #008B07)",
              color: "var(--Base-White, #FFF)",
              fontFamily: "HeadingNowVarTrial",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "24px",
            }}
          >
            {t("pages.home.submit")}
          </Button>

          <Dialog
            open={requestDialogOpen}
            onClose={() => setRequestDialogOpen(false)}
            sx={{
              "& .MuiPaper-root": {
                padding: "165px 0 87px",
                width: "90%",
                minWidth: "966px",
                alignItems: "center",
              },
            }}
          >
            <IconButton
              onClick={() => setRequestDialogOpen(false)}
              sx={{
                position: "absolute",
                top: "15px",
                left: "15px",
              }}
            >
              <ClosePopupIcon />
            </IconButton>
            <img src={RequestSentImg} width="173px" height="156px" style={{ marginBottom: "53px" }} />
            <Typography
              component="h3"
              sx={{
                //   fontFamily: "Inter",
                fontFamily: "Almarai",
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: 700,
                lineHeight: "normal",
                color: "#000",
                textAlign: "center",
                marginBottom: "32px",
              }}
            >
              {t("pages.home.requestSent")}
            </Typography>
            <Typography
              component="p"
              sx={{
                //   fontFamily: "Inter",
                fontFamily: "Almarai",
                fontSize: "20px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
                color: "#000",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              {t("pages.home.requestEndMessage")}
            </Typography>
            <Button
              variant="primary-lg"
              sx={{ width: "540px", borderRadius: "8px", background: "var(--Green---100, #008B07) !important" }}
              onClick={() => setRequestDialogOpen(false)}
              autoFocus
            >
              {t("pages.home.dismiss")}
            </Button>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
});

export default RegisterEntity;
